const STORAGE_PREFIX = 'site-announcement-dismissed';

const toDate = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toISOStringOrNull = (value) => {
    const date = toDate(value);
    return date ? date.toISOString() : null;
};

const getEffectiveExpiresAt = (item) => {
    const source = item?.attributes ?? item ?? {};
    const explicitExpiresAt = toDate(source.expiresAt);

    if (explicitExpiresAt) {
        return explicitExpiresAt.toISOString();
    }

    const durationMinutes = Number(source.durationMinutes);
    if (!Number.isFinite(durationMinutes)) {
        return null;
    }

    const baseDate = toDate(source.startAt) || toDate(source.createdAt) || toDate(source.updatedAt);
    if (!baseDate) {
        return null;
    }

    return new Date(baseDate.getTime() + durationMinutes * 60 * 1000).toISOString();
};

const getAnnouncementStatus = (announcement, now = new Date()) => {
    const startAt = toDate(announcement.startAt);
    const expiresAt = toDate(announcement.expiresAt) || toDate(announcement.effectiveExpiresAt);

    if (startAt && now < startAt) {
        return 'scheduled';
    }

    if (expiresAt && now > expiresAt) {
        return 'expired';
    }

    if (announcement.isActive === false) {
        return 'inactive';
    }

    return 'active';
};

const normalizeAnnouncement = (item) => {
    const attributes = item?.attributes ?? item ?? {};
    return {
        id: String(item?.id ?? attributes.id ?? attributes._id ?? ''),
        title: typeof attributes.title === 'string' ? attributes.title.trim() : '',
        message: typeof attributes.message === 'string' ? attributes.message.trim() : '',
        type: ['error', 'success', 'warning', 'info'].includes(attributes.type) ? attributes.type : 'info',
        priority: Number.isFinite(Number(attributes.priority)) ? Number(attributes.priority) : 0,
        startAt: toISOStringOrNull(attributes.startAt),
        expiresAt: toISOStringOrNull(attributes.expiresAt),
        effectiveExpiresAt: getEffectiveExpiresAt(item),
        durationMinutes: Number.isFinite(Number(attributes.durationMinutes)) ? Number(attributes.durationMinutes) : null,
        actionLabel: typeof attributes.actionLabel === 'string' ? attributes.actionLabel.trim() : '',
        actionUrl: typeof attributes.actionUrl === 'string' ? attributes.actionUrl.trim() : '',
        publishedAt: toISOStringOrNull(attributes.publishedAt),
        updatedAt: toISOStringOrNull(attributes.updatedAt),
        createdAt: toISOStringOrNull(attributes.createdAt),
        isActive: attributes.isActive !== false,
        status: attributes.status || getAnnouncementStatus({
            startAt: attributes.startAt,
            expiresAt: attributes.expiresAt,
            effectiveExpiresAt: getEffectiveExpiresAt(item),
            isActive: attributes.isActive,
        }),
    };
};

const safeStorage = () => {
    try {
        return window.localStorage;
    } catch (error) {
        return null;
    }
};

const getVersionToken = (announcement) => (
    announcement.updatedAt || announcement.publishedAt || announcement.createdAt || announcement.startAt || announcement.effectiveExpiresAt || '0'
);

const getDismissalStoreKey = (userId = 'guest') => `${STORAGE_PREFIX}:${userId}`;

const loadDismissalStore = (userId = 'guest') => {
    const storage = safeStorage();
    if (!storage) {
        return { storage: null, store: { dismissed: {} } };
    }

    try {
        const rawValue = storage.getItem(getDismissalStoreKey(userId));
        if (!rawValue) {
            return { storage, store: { dismissed: {} } };
        }

        const parsed = JSON.parse(rawValue);
        return {
            storage,
            store: parsed && typeof parsed === 'object' && parsed.dismissed && typeof parsed.dismissed === 'object'
                ? parsed
                : { dismissed: {} },
        };
    } catch (error) {
        return { storage, store: { dismissed: {} } };
    }
};

const saveDismissalStore = (storage, userId, store) => {
    if (!storage) {
        return;
    }

    storage.setItem(getDismissalStoreKey(userId), JSON.stringify(store));
};

const pruneDismissalStore = (userId, currentAnnouncements = []) => {
    const { storage, store } = loadDismissalStore(userId);
    if (!storage) {
        return;
    }

    const currentVersionMap = new Map(currentAnnouncements.map((announcement) => [announcement.id, getVersionToken(announcement)]));
    const previousDismissed = store.dismissed || {};
    const nextDismissed = {};

    Object.entries(previousDismissed).forEach(([announcementId, entry]) => {
        if (!entry || typeof entry !== 'object') {
            return;
        }

        const currentVersion = currentVersionMap.get(announcementId);
        if (!currentVersion || currentVersion !== entry.versionToken) {
            return;
        }

        nextDismissed[announcementId] = entry;
    });

    if (Object.keys(nextDismissed).length !== Object.keys(previousDismissed).length) {
        saveDismissalStore(storage, userId, {
            ...store,
            dismissed: nextDismissed,
        });
    }
};

export const getAnnouncementStorageKey = (_announcement, userId = 'guest') => getDismissalStoreKey(userId);

export const dismissAnnouncement = (announcement, userId = 'guest') => {
    const { storage, store } = loadDismissalStore(userId);
    if (!storage || !announcement?.id) {
        return;
    }

    store.dismissed[announcement.id] = {
        versionToken: getVersionToken(announcement),
        dismissedAt: new Date().toISOString(),
    };

    saveDismissalStore(storage, userId, store);
};

export const isAnnouncementDismissed = (announcement, userId = 'guest') => {
    const { store } = loadDismissalStore(userId);
    if (!announcement?.id) {
        return false;
    }

    return store.dismissed?.[announcement.id]?.versionToken === getVersionToken(announcement);
};

// toDate is defined above so it can safely reject invalid date strings.

export const isAnnouncementActive = (announcement, now = new Date()) => {
    if (!announcement.id || !announcement.message) {
        return false;
    }

    return getAnnouncementStatus(announcement, now) === 'active';
};

export const fetchActiveAnnouncements = async (userId = 'guest') => {
    const response = await fetch('/api/announcements', {
        credentials: 'include',
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch announcements');
    }

    const data = await response.json();
    const items = Array.isArray(data?.announcements) ? data.announcements : [];
    const now = new Date();
    const announcements = items.map(normalizeAnnouncement);

    pruneDismissalStore(userId, announcements);

    return announcements
        .filter((announcement) => isAnnouncementActive(announcement, now))
        .filter((announcement) => !isAnnouncementDismissed(announcement, userId))
        .sort((left, right) => {
            if (right.priority !== left.priority) {
                return right.priority - left.priority;
            }

            const leftDate = new Date(left.updatedAt || left.createdAt || left.startAt || 0).getTime();
            const rightDate = new Date(right.updatedAt || right.createdAt || right.startAt || 0).getTime();
            return rightDate - leftDate;
        });
};

export const getAllAnnouncements = async () => {
    const response = await fetch('/api/admin/announcements', {
        credentials: 'include',
    });

    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to fetch announcements');
    }

    const data = await response.json();
    return Array.isArray(data?.announcements) ? data.announcements.map(normalizeAnnouncement) : [];
};

export const createAnnouncement = async (payload) => {
    const response = await fetch('/api/admin/announcements', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || 'Failed to create announcement');
    }

    return data.announcement ? normalizeAnnouncement(data.announcement) : null;
};

export const updateAnnouncement = async (id, payload) => {
    const response = await fetch(`/api/admin/announcements/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || 'Failed to update announcement');
    }

    return data.announcement ? normalizeAnnouncement(data.announcement) : null;
};

export const deleteAnnouncement = async (id) => {
    const response = await fetch(`/api/admin/announcements/${id}`, {
        method: 'DELETE',
        credentials: 'include',
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || 'Failed to delete announcement');
    }

    return data.announcement ? normalizeAnnouncement(data.announcement) : null;
};

export const clearExpiredAnnouncements = async () => {
    const response = await fetch('/api/admin/announcements/expired', {
        method: 'DELETE',
        credentials: 'include',
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || 'Failed to clear expired announcements');
    }

    return data;
};

export { getEffectiveExpiresAt };
export { getAnnouncementStatus };
